// Text offsets for highlights (BRIEFS C5.1).
//
// The server stores a highlight as character offsets in a point's `plain_text`, which is
// made by backend/app/services/v2_html.plain_text: tags removed, a space at <br>, <tr>,
// <td>, <th>, <table> (and p/div/li), entities decoded, every run of white space made one
// space, spaces at both ends cut off. The functions here walk a point's DOM the SAME way,
// so an offset means the same letter in the browser and on the server.
//
//   textMap(root)                 → { text, at: [rawIndex per letter], raw: [...] }
//   offsetsInPoint(range, root)   → { start, end } in plain_text (null when the range
//                                    does not touch the point's words)
//   markHtml(html, highlights)    → the point's html with <mark data-hl="id"> around the
//                                    highlighted words (a highlight may cross tags)

const BREAK_TAGS = new Set(['BR', 'TR', 'TD', 'TH', 'TABLE', 'P', 'DIV', 'LI']);

// Python's str.isspace() / re \s: JS \s plus the separators Python also counts,
// minus U+FEFF (not white space in Python).
function isWs(ch) {
  if (ch === '﻿') return false;
  return /\s/.test(ch) || ch === '\x1C' || ch === '\x1D' || ch === '\x1E' || ch === '\x1F' || ch === '\x85';
}

// Raw letters in document order. Each raw item: { node, offset } for a letter of a text
// node, or { node: null } for the space a break tag adds.
function rawItems(root) {
  const raw = [];
  const starts = new Map();   // node → raw index where it starts
  const ends = new Map();     // node → raw index after it
  const walk = (node) => {
    starts.set(node, raw.length);
    if (node.nodeType === 3) {
      const s = node.data;
      for (let i = 0; i < s.length; i += 1) raw.push({ node, offset: i, ch: s[i] });
    } else if (node.nodeType === 1) {
      const brk = node !== root && BREAK_TAGS.has(node.tagName);
      if (brk) raw.push({ node: null, ch: ' ' });
      for (let c = node.firstChild; c; c = c.nextSibling) walk(c);
      if (brk && node.tagName !== 'BR') raw.push({ node: null, ch: ' ' });
    }
    ends.set(node, raw.length);
  };
  walk(root);
  return { raw, starts, ends };
}

export function textMap(root) {
  const { raw, starts, ends } = rawItems(root);
  let text = '';
  const at = [];           // at[k] = raw index of plain-text letter k
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i].ch;
    if (isWs(ch)) {
      if (text.length && text[text.length - 1] !== ' ') { text += ' '; at.push(i); }
    } else {
      text += ch;
      at.push(i);
    }
  }
  if (text.endsWith(' ')) { text = text.slice(0, -1); at.pop(); }
  return { text, at, raw, starts, ends };
}

// Raw index of a DOM boundary point (container, offset) inside root.
function rawIndexOf(map, root, container, offset) {
  if (container.nodeType === 3) {
    const s = map.starts.get(container);
    return s == null ? null : s + offset;
  }
  const kids = container.childNodes;
  if (offset < kids.length) {
    const s = map.starts.get(kids[offset]);
    return s == null ? null : s;
  }
  const e = map.ends.get(container);
  return e == null ? null : e;
}

// Number of plain-text letters before raw index r (binary search in map.at).
function plainIndex(map, r) {
  let lo = 0;
  let hi = map.at.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (map.at[mid] < r) lo = mid + 1; else hi = mid;
  }
  return lo;
}

// { start, end } (plain_text offsets, end exclusive) of the part of `range` inside `root`.
export function offsetsInPoint(range, root) {
  if (!range || !root || !range.intersectsNode(root)) return null;
  const map = textMap(root);
  const len = map.text.length;
  let start = 0;
  let end = len;
  if (root.contains(range.startContainer)) {
    const r = rawIndexOf(map, root, range.startContainer, range.startOffset);
    if (r != null) start = plainIndex(map, r);
  } else if (range.comparePoint(root, 0) < 0) {
    return null;    // the range starts after this point
  }
  if (root.contains(range.endContainer)) {
    const r = rawIndexOf(map, root, range.endContainer, range.endOffset);
    if (r != null) end = plainIndex(map, r);
  } else if (range.comparePoint(root, root.childNodes.length) > 0) {
    return null;    // the range ends before this point
  }
  // spaces at both ends are not highlighted (the server trims them as well)
  while (start < end && map.text[start] === ' ') start += 1;
  while (end > start && map.text[end - 1] === ' ') end -= 1;
  if (start >= end) return null;
  return { start, end, text: map.text.slice(start, end) };
}

// Wrap plain-text range [start, end) of root in <mark> elements (one per text node piece).
function wrapRange(root, start, end, attrs) {
  const map = textMap(root);
  if (start >= end || start >= map.at.length) return;
  const rs = map.at[start];
  const re = map.at[Math.min(end, map.at.length) - 1] + 1;
  // group raw letters [rs, re) by text node
  const pieces = [];
  for (let i = rs; i < re; i += 1) {
    const it = map.raw[i];
    if (!it.node) continue;
    const last = pieces[pieces.length - 1];
    if (last && last.node === it.node && last.to === it.offset) last.to += 1;
    else pieces.push({ node: it.node, from: it.offset, to: it.offset + 1 });
  }
  // right to left inside each node keeps earlier offsets valid
  for (let k = pieces.length - 1; k >= 0; k -= 1) {
    const { node, from, to } = pieces[k];
    if (/^\s*$/.test(node.data.slice(from, to)) && (node.parentNode && /^(TR|TABLE|TBODY|THEAD)$/.test(node.parentNode.tagName))) continue;
    let mid = node;
    if (from > 0) mid = node.splitText(from);
    if (to - from < mid.data.length) mid.splitText(to - from);
    const mark = document.createElement('mark');
    Object.keys(attrs).forEach((a) => mark.setAttribute(a, attrs[a]));
    mid.parentNode.insertBefore(mark, mid);
    mark.appendChild(mid);
  }
}

export function markHtml(html, highlights) {
  if (!highlights || highlights.length === 0) return html;
  const box = document.createElement('div');
  box.innerHTML = html;   // sanitised on the server (v2_html.sanitize)
  // review ones (usually the whole point) first, so normal marks sit inside them
  const list = [...highlights].sort((a, b) => (Number(b.needs_review) - Number(a.needs_review)) || (a.start - b.start));
  list.forEach((h) => {
    wrapRange(box, h.start, h.end, {
      'data-hl': String(h.id),
      class: h.needs_review ? 'v2-hl v2-hl-review' : 'v2-hl',
      title: h.needs_review ? 'The words here changed. Click to check your highlight.' : 'Click to remove this highlight',
    });
  });
  return box.innerHTML;
}
